import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Editor integration",
  "description": "Making the most of Turborepo"
};
export let structuredData = {
  "contents": [{
    "heading": "",
    "content": "To get the best experience with turbo, Turborepo provides a few utilities for integrating with your editor."
  }, {
    "heading": "json-schema-for-turbojson",
    "content": "Turborepo uses JSON Schema to give you auto-complete in your\nturbo.json file. By including the $schema key in your turbo.json, your editor is able to\nprovide full documentation and linting in case you have invalid shapes or missing keys."
  }, {
    "heading": "sourcing-from-the-web",
    "content": "A schema.json is accessible at the URL shown below. This has the advantage of not needing to run your package manager's install command to see in-editor validation."
  }, {
    "heading": "sourcing-from-the-web",
    "content": "There is also a major versioned schema.json available, following the format of https://turborepo.com/schema.<version>.json."
  }, {
    "heading": "sourcing-from-node_modules",
    "content": "Starting in Turborepo 2.4, schema.json is available in node_modules once you've run your package manager's install command:"
  }, {
    "heading": "sourcing-from-node_modules",
    "content": "We recommend installing turbo at the root of your repository, so the path\nfor the schema should point to node_modules at the root of your repository.\nIn Package Configurations, you may\nneed to use a path like ../../node_modules/turbo/schema.json."
  }, {
    "heading": "linting-for-environment-variables",
    "content": "Handling environment variables is an important part of building applications in a Turborepo."
  }, {
    "heading": "linting-for-environment-variables",
    "content": "The eslint-config-turbo package extends your ESLint setup to help you make sure you've taken care of all of your environment variables."
  }, {
    "heading": "turborepo-lsp",
    "content": "Enable even more auto-complete and linting than provided by JSON Schema, with in-editor hinting for invalid globs, references to non-existent tasks or packages, and\nextra repository visibility tools."
  }, {
    "heading": "turborepo-lsp",
    "content": "Visit the VSCode Extension Marketplace to install."
  }, {
    "heading": "turborepo-lsp",
    "content": "The language server can be used on any editors that support the Language\nServer Protocol. Log a\nrequest in our issue tracker to\nexpress your interest."
  }],
  "headings": [{
    "id": "json-schema-for-turbojson",
    "content": "JSON Schema for turbo.json"
  }, {
    "id": "sourcing-from-the-web",
    "content": "Sourcing from the web"
  }, {
    "id": "sourcing-from-node_modules",
    "content": "Sourcing from node_modules"
  }, {
    "id": "linting-for-environment-variables",
    "content": "Linting for environment variables"
  }, {
    "id": "turborepo-lsp",
    "content": "Turborepo LSP"
  }]
};
import {Callout} from '#components/callout';
export const toc = [{
  depth: 2,
  url: "#json-schema-for-turbojson",
  title: _jsxs(_Fragment, {
    children: ["JSON Schema for ", _jsx("code", {
      children: "turbo.json"
    })]
  })
}, {
  depth: 3,
  url: "#sourcing-from-the-web",
  title: _jsx(_Fragment, {
    children: "Sourcing from the web"
  })
}, {
  depth: 3,
  url: "#sourcing-from-node_modules",
  title: _jsxs(_Fragment, {
    children: ["Sourcing from ", _jsx("code", {
      children: "node_modules"
    })]
  })
}, {
  depth: 2,
  url: "#linting-for-environment-variables",
  title: _jsx(_Fragment, {
    children: "Linting for environment variables"
  })
}, {
  depth: 2,
  url: "#turborepo-lsp",
  title: _jsx(_Fragment, {
    children: "Turborepo LSP"
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h2: "h2",
    h3: "h3",
    p: "p",
    pre: "pre",
    span: "span",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(_components.p, {
      children: ["To get the best experience with ", _jsx(_components.code, {
        children: "turbo"
      }), ", Turborepo provides a few utilities for integrating with your editor."]
    }), "\n", _jsxs(_components.h2, {
      id: "json-schema-for-turbojson",
      children: ["JSON Schema for ", _jsx(_components.code, {
        children: "turbo.json"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Turborepo uses ", _jsx(_components.a, {
        href: "https://json-schema.org/",
        children: "JSON Schema"
      }), " to give you auto-complete in your\n", _jsx(_components.code, {
        children: "turbo.json"
      }), " file. By including the ", _jsx(_components.code, {
        children: "$schema"
      }), " key in your ", _jsx(_components.code, {
        children: "turbo.json"
      }), ", your editor is able to\nprovide full documentation and linting in case you have invalid shapes or missing keys."]
    }), "\n", _jsx(_components.h3, {
      id: "sourcing-from-the-web",
      children: "Sourcing from the web"
    }), "\n", _jsxs(_components.p, {
      children: ["A ", _jsx(_components.code, {
        children: "schema.json"
      }), " is accessible at the URL shown below. This has the advantage of not needing to run your package manager's install command to see in-editor validation."]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"$schema\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"https://turborepo.com/schema.json\""
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["There is also a major versioned ", _jsx(_components.code, {
        children: "schema.json"
      }), " available, following the format of ", _jsx(_components.code, {
        children: "https://turborepo.com/schema.<version>.json"
      }), "."]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"$schema\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"https://turborepo.com/schema.v1.json\""
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.h3, {
      id: "sourcing-from-node_modules",
      children: ["Sourcing from ", _jsx(_components.code, {
        children: "node_modules"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Starting in Turborepo 2.4, ", _jsx(_components.code, {
        children: "schema.json"
      }), " is available in ", _jsx(_components.code, {
        children: "node_modules"
      }), " once you've run your package manager's install command:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"$schema\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"./node_modules/turbo/schema.json\""
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(Callout, {
      title: "node_modules location",
      children: _jsxs(_components.p, {
        children: ["We recommend installing ", _jsx(_components.code, {
          children: "turbo"
        }), " at the root of your repository, so the path\nfor the schema should point to ", _jsx(_components.code, {
          children: "node_modules"
        }), " at the root of your repository.\nIn ", _jsx(_components.a, {
          href: "/docs/reference/package-configurations",
          children: "Package Configurations"
        }), ", you may\nneed to use a path like ", _jsx(_components.code, {
          children: "../../node_modules/turbo/schema.json"
        }), "."]
      })
    }), "\n", _jsx(_components.h2, {
      id: "linting-for-environment-variables",
      children: "Linting for environment variables"
    }), "\n", _jsx(_components.p, {
      children: "Handling environment variables is an important part of building applications in a Turborepo."
    }), "\n", _jsxs(_components.p, {
      children: [_jsxs(_components.a, {
        href: "/docs/reference/eslint-config-turbo",
        children: ["The ", _jsx(_components.code, {
          children: "eslint-config-turbo"
        }), " package"]
      }), " extends your ESLint setup to help you make sure you've taken care of all of your environment variables."]
    }), "\n", _jsx(_components.h2, {
      id: "turborepo-lsp",
      children: "Turborepo LSP"
    }), "\n", _jsx(_components.p, {
      children: "Enable even more auto-complete and linting than provided by JSON Schema, with in-editor hinting for invalid globs, references to non-existent tasks or packages, and\nextra repository visibility tools."
    }), "\n", _jsxs(_components.p, {
      children: ["Visit the ", _jsx(_components.a, {
        href: "https://marketplace.visualstudio.com/items?itemName=Vercel.turbo-vsc",
        children: "VSCode Extension Marketplace"
      }), " to install."]
    }), "\n", _jsx(Callout, {
      type: "info",
      title: "Not using Visual Studio Code?",
      children: _jsxs(_components.p, {
        children: ["The language server can be used on any editors that support the ", _jsx(_components.a, {
          href: "https://microsoft.github.io/language-server-protocol/",
          children: "Language\nServer Protocol"
        }), ". Log a\nrequest in our ", _jsx(_components.a, {
          href: "https://github.com/vercel/turborepo/issues",
          children: "issue tracker"
        }), " to\nexpress your interest."]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
